import { type ElementType, StrictMode, useEffect, type ComponentProps } from 'react';
import { createRoot } from 'react-dom/client';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ErrorBoundary } from 'react-error-boundary';
import { Box, Container, SkeletonCircle, SkeletonText, Stack } from '@chakra-ui/react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';

import { reportWebVitals } from '../../../../modules/core/utils/reportWebVitals';
import { App } from '../../../../modules/app/components/App';

function MPA({ children }: ComponentProps<any>) {
    const { hash } = useLocation();

    useEffect(() => {
        const elements = [
            ...document.querySelectorAll('.main-header, .mobile-header, .banners-wrapper'),
        ];
        elements.forEach((element) => {
            (element as HTMLElement).style.visibility = 'visible';
        });
    }, []);

    useEffect(() => {
        if (hash !== '') {
            const element = document.querySelector(hash);
            if (element !== null) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }, [hash]);

    return children;
}

function FallbackMPA() {
    return (
        <Box bg="#fff" py={8}>
            <Container maxW="container.xl">
                <Stack gap={3} p={6}>
                    <SkeletonCircle size="11" />
                    <SkeletonText mt={4} noOfLines={4} spacing={4} skeletonHeight={4} />
                </Stack>
            </Container>
        </Box>
    );
}

/**
 * Render react component with necessary providers.
 *
 * @remarks
 * Use providers from App component.
 * Seek for #root and render in there.
 * In development process Web Vitals are reported to the console.
 *
 * @see {@link App}
 *
 * @param Page - page component to render
 */
export function createMPA(Page: ElementType) {
    const root = createRoot(document.getElementById('root') as HTMLElement);

    root.render(
        <StrictMode>
            <Router>
                <MPA>
                    <App>
                        <ErrorBoundary fallback={<FallbackMPA />}>
                            <Page />
                        </ErrorBoundary>
                        <ReactQueryDevtools initialIsOpen={false} />
                    </App>
                </MPA>
            </Router>
        </StrictMode>,
    );

    if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        reportWebVitals(console.log);
    }
}
